import { actionsDisabled } from '../../utils/actionsDisabled';
import { DefaultColumns } from './data';
import { fetchTendersByStatus, manuallyTenderShipments, releaseOrReTender } from '../../services/shipmentApi';
import { GridColDef } from '@mui/x-data-grid-premium';
import { ReleaseOrRetenderModal } from '../../components/controls/modals/releaseOrRetenderModal';
import { ReleaseTenderButton } from '../../components/controls/buttons/releaseTenderButton';
import { removeOrAddColumn, reorderColumn } from '../../utils/columnReorder';
import { StandardBoard } from '../../components/standardBoard';
import { Tender } from '../../models/tender';
import { useAuth } from '../../auth';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { TenderShipmentButton } from '../../components/controls/buttons/tenderShipmentButton';
import { Divider } from '@mui/material';
import { RefreshButton } from '../../components/controls/buttons/refreshButton';
import { ManualTenderModal } from '../../components/controls/modals/manualTenderModal';
import { ManualTender } from '../../models/ManualTender';
import { columnDataFormatter } from '../../utils/columnDataFormatter';

export function TenderReviewBoard() {
    const columnsStorageKey = 'tenderReviewBoardColumns';

    useEffect(() => {
        if (localStorage.getItem(columnsStorageKey)) {
            let columnObj = localStorage.getItem(columnsStorageKey);
            let obj = columnObj && JSON.parse(columnObj);

            if (obj) {
                let formattedColumns = columnDataFormatter(obj);
                setColumns(formattedColumns);
            }
        }
    }, [])

    const [isLoading, setIsLoading] = useState(true);
    const [selectedShipments, setSelectedShipments] = useState<number[]>([]);
    const [tenders, setTenders] = useState<Tender[]>([]);
    const [isColumnsDialogOpen, setColumnsOpen] = useState(false);
    const [releaseTenderModalOpen, setReleaseTenderModalOpen] = useState(false);
    const [manualTenderModalOpen, setManualTenderModalOpen] = useState(false);
    const [columns, setColumns] = useState<GridColDef[]>(DefaultColumns);

    const auth = useAuth();

    const fetchData = useCallback(async () => {

        const data = await fetchTendersByStatus('TenderReview');
        setTenders(data);
        setIsLoading(false);
    }, []);

    async function tender() {

        setReleaseTenderModalOpen(false);

        setIsLoading(true);
        await releaseOrReTender(selectedShipments);
        await fetchData();
        setIsLoading(false);
    }

    const manualTenderShipments = async (comment: string) => {

        setManualTenderModalOpen(false);
        setIsLoading(true);

        let mt: ManualTender = {
            shipmentIDs: selectedShipments,
            comment: comment
        };

        await manuallyTenderShipments(mt);

        fetchData();
        setIsLoading(false);

    }

    const onColumnAddedRemoved = (column: any) => {
        let updatedColumns = removeOrAddColumn(columns, column, columnsStorageKey);
        setColumns(updatedColumns);
    }

    const onColumnIndexChange = (columnPropertyName: string, targetIndex: number) => {
        let updatedColumns = reorderColumn(columns, columnPropertyName, targetIndex, columnsStorageKey);
        setColumns(updatedColumns);
    }

    useEffect(() => {
        fetchData()
            .catch(console.error);;
    }, [fetchData])

    function onColumnsDialogClose() {
        setColumnsOpen(false);
        setManualTenderModalOpen(false);
        setReleaseTenderModalOpen(false);
    }

    const secondaryElement = (
        <Fragment>
            <TenderShipmentButton disabled={actionsDisabled(selectedShipments)} title='Manually tender the selected shipments.' onClick={() => setManualTenderModalOpen(true)} />
            <Divider style={{ margin: '5px' }} orientation='vertical' />
            <RefreshButton title='Refresh the current report list' onClick={() => fetchData()} />
        </Fragment>
    );

    return (
        <StandardBoard
            onRefreshButtonClick={fetchData}
            title='Tender Review Board'
            primaryElement={<ReleaseTenderButton disabled={actionsDisabled(selectedShipments)} title='Release the selected shipments for tender' onClick={() => setReleaseTenderModalOpen(true)} />}            
            secondaryElement={secondaryElement}
            gridProps={{
                filterStorageObject: 'reviewBoardFilters',
                groupingStorageObject: 'reviewBoardGrouping',                
                setSelectedShipments: setSelectedShipments,
                tenders: tenders,
                onColumnIndexChange,
            }}
            columns={columns}
            isColumnsDialogOpen={isColumnsDialogOpen}
            onColumnsDialogChanged={onColumnAddedRemoved}
            onColumnsDialogClose={onColumnsDialogClose}
            isBackdropVisible={isLoading}
            onColumnButtonClick={() => setColumnsOpen(true)}
        >
            <ManualTenderModal open={manualTenderModalOpen} close={onColumnsDialogClose} confirmManualTender={manualTenderShipments} />
            <ReleaseOrRetenderModal retender={false} open={releaseTenderModalOpen} close={onColumnsDialogClose} confirmReleaseOrTender={tender} />
        </StandardBoard>
    );
}